'use client';

import { Export } from '@tiptap-pro/extension-export';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';

import { Transition } from '@headlessui/react';
import { Button, Spinning, Typography } from 'components';
import { FALLBACK_GRAVATAR } from 'constants/images';
import { AnimatePresence, motion } from 'framer-motion';
import { IntegrationEnum } from 'gql/graphql';
import {
  BaloonBigIcon,
  BaloonSmallIcon,
  CopyLightIcon,
  DownloadIcon,
  EditChatIcon,
  RegenerationIcon,
  TranslationIcon
} from 'lib/svgs/Icons';
import Markdown from 'markdown-to-jsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { cn } from 'utils/cn';
import FileAttached from './Attached';

interface IChatMsgProps {
  message?: string;
  title?: string;
  index?: number;
  file?: File | null;
  assistant?: boolean;
  messageAnimated?: boolean;
  chatId?: string;
  onEdit?: () => void;
  stopAnimation?: () => void;
  scrollToBottom?: () => void;
}

export default function ChatMsg({
  message: messageIncoming = '',
  messageAnimated,
  title,
  chatId,
  file,
  index,
  assistant,
  onEdit,
  stopAnimation,
  scrollToBottom
}: IChatMsgProps) {
  const { data } = useCurrentUser();
  const router = useRouter();
  const t = useTranslations();

  const [logoAnimated, setLogoAnimated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [spin, setSpin] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [wordsIndex, setWordsIndex] = useState(0);

  const [downloading, setDownloading] = useState(false);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Heading,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Bold,
      Highlight,
      Italic,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
      }),
      BulletList,
      ListItem,
      OrderedList,
      Export.configure({
        appId: process.env.NEXT_PUBLIC_TIPTAP_APP_ID,
        token: process.env.NEXT_PUBLIC_TIPTAP_JWT_TOKEN
      })
    ],
    content: ``,
    editorProps: {}
  });

  const handleDownload = useCallback(() => {
    setDownloading(true);
    editor?.commands.setContent(messageIncoming ?? '');
    editor
      ?.chain()
      ?.export({
        format: 'docx',
        onExport: (context) => {
          if (context?.error) notifyException(context?.error, ESpansException.AiException);

          const element = document.createElement('a');
          const file = context?.blob as Blob;
          element.href = URL.createObjectURL(file);
          element.download = `${title} - ${index}.docx`;
          document.body.appendChild(element);
          element.click();
          setDownloading(false);
        }
      })
      ?.run();
  }, [editor, messageIncoming, title]);

  const messageReplaced = messageIncoming.replace(/\n/g, '<br/>');
  const messagesResult = messageReplaced.split(/(?= )/);

  const imageUrl = useMemo(
    () => `https://gravatar.com/avatar/${data?.gravatarEmailHash}?d=${FALLBACK_GRAVATAR}&s=${200}`,
    [data?.gravatarEmailHash]
  );

  const onCreateTranslation = useCallback(() => {
    if (chatId) {
      router.push(`/translation/new/${IntegrationEnum.ChatAi}?id=${chatId}&index=${index}`);
    }
  }, [router, chatId]);

  const onCopy = useCallback(() => {
    if (messageIncoming) {
      // Replace <br/> tags with actual line breaks
      const formattedText = messageIncoming.replace(/<br\/?>/g, '\n');

      // Check if the Clipboard API is supported
      if (navigator.clipboard) {
        navigator.clipboard.writeText(formattedText);
      } else {
        // Fallback for browsers that don't support the Clipboard API
        const tempTextArea = document.createElement('textarea');
        tempTextArea.value = formattedText;
        document.body.appendChild(tempTextArea);
        tempTextArea.focus();
        tempTextArea.select();
        document.execCommand('copy');
        document.body.removeChild(tempTextArea);
      }

      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  }, [messageIncoming]);

  const onRegenerate = () => {
    setSpin(true);
    setTimeout(() => {
      setSpin(false);
    }, 1000);
  };

  const ButtonCopy = useCallback(
    () => (
      <a data-tooltip-id="tolq-tooltip" data-tooltip-content={t('buttons.copy')}>
        <Button
          size="small"
          variant="icon"
          color="none"
          className="!m-0"
          onClick={onCopy}
          startAdornment={
            <AnimatePresence mode="wait">
              <motion.div
                transition={{ ease: 'easeOut', duration: 0.2 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {!copied ? (
                  <CopyLightIcon className="!text-tertiary-600 hover:!text-primary-blue-100" />
                ) : (
                  <Typography variant="light4" className="text-tertiary-200 ">
                    {t('chat.copied')}
                  </Typography>
                )}
              </motion.div>
            </AnimatePresence>
          }
        />
      </a>
    ),
    [onCopy, t, copied]
  );

  const ButtonCreateTranslation = useCallback(
    () => (
      <a
        data-tooltip-id="tolq-tooltip"
        data-tooltip-content={t('buttons.create_translation_request')}
      >
        <Button
          size="small"
          variant="icon"
          color="none"
          className="!m-0"
          onClick={onCreateTranslation}
          startAdornment={
            <TranslationIcon className="ml-1 mt-2 scale-150 !text-tertiary-600 hover:!text-primary-blue-100" />
          }
        />
      </a>
    ),
    [onCreateTranslation, t, copied]
  );

  const ButtonRegenerate = () => {
    return null;

    return (
      <a data-tooltip-id="tolq-tooltip" data-tooltip-content={t('buttons.regenerate')}>
        <Button
          size="small"
          variant="icon"
          color="none"
          className="!m-0"
          onClick={onRegenerate}
          startAdornment={
            <RegenerationIcon
              className={cn(
                spin ? 'animate-spin' : '',
                '!text-tertiary-600 hover:!text-primary-blue-100'
              )}
            />
          }
        />
      </a>
    );
  };

  const ButtonEdit = () => (
    <a data-tooltip-id="tolq-tooltip" data-tooltip-content={t('buttons.edit')}>
      <Button
        size="small"
        variant="icon"
        color="none"
        className="!m-0"
        onClick={onEdit}
        startAdornment={
          <EditChatIcon
            className={cn(spin ? 'wobble' : '', '!text-tertiary-600 hover:!text-primary-blue-100')}
          />
        }
      />
    </a>
  );

  const ButtonDownload = () => (
    <a data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.download')}>
      <Button
        size="small"
        variant="icon"
        color="none"
        className="!m-0"
        onClick={handleDownload}
        startAdornment={
          downloading ? (
            <div className="relative mb-6 mt-0">
              <Spinning className={cn('top-[2px]')} />
            </div>
          ) : (
            <DownloadIcon className="ml-0.5 scale-150 !text-tertiary-600 hover:!text-primary-blue-100" />
          )
        }
      />
    </a>
  );

  useEffect(() => {
    if (messageAnimated) {
      if (currentIndex > messagesResult?.length) {
        stopAnimation?.();
        scrollToBottom?.();
      }

      if (currentIndex < messagesResult.length) {
        const timeout = setTimeout(() => {
          setCurrentText((prevText) => `${prevText} ${messagesResult[currentIndex]}`);
          setCurrentIndex((prevIndex) => prevIndex + 1);
        }, 20);

        return () => {
          clearTimeout(timeout);
        };
      }
    }
  }, [
    currentIndex,
    messagesResult,
    setCurrentText,
    setCurrentIndex,
    scrollToBottom,
    stopAnimation,
    messageAnimated,
    assistant
  ]);

  useEffect(() => {
    if (messageAnimated && assistant && currentText?.length / 100 > wordsIndex + 1) {
      scrollToBottom?.();
      setWordsIndex((oldState) => oldState + 1);
    }
  }, [messageAnimated, assistant, currentText?.length, wordsIndex, setWordsIndex, scrollToBottom]);

  useEffect(() => {
    if (messageAnimated) setLogoAnimated(true);
  }, [messageAnimated, setLogoAnimated]);

  useEffect(() => {
    if (!(currentIndex < messagesResult?.length)) {
      const timeout = setTimeout(() => {
        setLogoAnimated(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, messagesResult, setLogoAnimated]);

  return (
    <div className="grid grid-cols-[32px__1fr_32px] gap-4">
      <div className="col-span-1">
        {assistant ? (
          <Image
            alt="ai-icon"
            className=" h-8 shrink-0 rounded-full bg-white"
            height={32}
            width={32}
            src={'/images/icons/ai_light.svg'}
            priority={false}
          />
        ) : (
          <div className="mx-1 flex h-full flex-col items-end justify-end">
            <ButtonCopy />
            <ButtonEdit />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {file?.name && (
          <div className="flex justify-end">
            <FileAttached filename={file?.name} className="!rounded-tr-none">
              {file?.name}
            </FileAttached>
          </div>
        )}
        <div
          className={cn(
            'col-span-1',
            'rounded-3xl border p-3',
            assistant ? 'rounded-tl-none bg-white' : 'h-fit rounded-tr-none bg-quinary-900'
          )}
        >
          <Typography variant="regular3" className="text-tertiary-100 ">
            <span>
              <Markdown>{messageAnimated ? currentText : messageReplaced}</Markdown>
              {assistant && messageAnimated && (
                <Transition
                  show={logoAnimated}
                  as={Fragment}
                  enter={'transition ease-out duration-200'}
                  enterFrom={`opacity-0 translate-y-1`}
                  enterTo={'opacity-100 translate-y-0'}
                  leave={'transition ease-out duration-[0.5s]'}
                  leaveFrom={'opacity-100 translate-y-0'}
                  leaveTo={`opacity-0 translate-y-1`}
                >
                  <span className="inline-block align-middle">
                    <div
                      className={cn(
                        'absolute left-[16px] ml-6 mr-20 flex h-full w-12 flex-1 items-center justify-center',
                        ''
                      )}
                    >
                      <span className={cn('relative bottom-0.5 right-[24px]')}>
                        <div className="animate-bounce-small-re transition-all ease-in-out">
                          <BaloonBigIcon className="scale-[0.2]" />
                        </div>
                      </span>
                      <span className={cn('relative -bottom-0.5 right-[82px]')}>
                        <div className="animate-bounce-small transition-all ease-in-out">
                          <BaloonSmallIcon className="scale-[0.2]" />
                        </div>
                      </span>
                    </div>
                  </span>
                </Transition>
              )}
            </span>
          </Typography>
        </div>
      </div>
      <div className="col-span-1">
        {assistant ? (
          <div className="mx-1 flex h-full flex-col items-start justify-end">
            <ButtonCreateTranslation />
            <ButtonDownload />
            <ButtonCopy />
            <ButtonRegenerate />
          </div>
        ) : (
          <Image
            alt="user-img"
            className=" h-8 shrink-0 rounded-full bg-white"
            height={32}
            width={32}
            src={imageUrl}
            priority={false}
          />
        )}
      </div>
    </div>
  );
}
